import { useEffect, useMemo, useState, useCallback } from "react";
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable } from "@dnd-kit/core";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const PRIORITY_DOT = { low: "bg-muted-foreground/40", normal: "bg-primary/50", high: "bg-warning", urgent: "bg-destructive" };

function iso(d) { return d.toISOString().slice(0, 10); }
function monthGrid(year, month) {
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const start = new Date(year, month, 1 - startOffset);
  return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
}

function EventChip({ ev }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `${ev.id}::${ev.date}`, data: ev, disabled: ev.recurring });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes} data-testid={`cal-ev-${ev.id}`}
      className={cn("mb-1 flex items-center gap-1 truncate rounded px-1 py-0.5 text-[11px]",
        ev.recurring ? "cursor-default bg-accent-subtle text-accent" : "cursor-grab bg-muted text-foreground active:cursor-grabbing",
        isDragging && "opacity-50", ev.status === "done" && "line-through opacity-60")}>
      <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", PRIORITY_DOT[ev.priority])} />
      <span className="truncate">{ev.title}{ev.recurring ? " ↻" : ""}</span>
    </div>
  );
}

function DayCell({ day, inMonth, isToday, events }) {
  const { setNodeRef, isOver } = useDroppable({ id: `day::${iso(day)}` });
  return (
    <div ref={setNodeRef} data-testid={`cal-day-${iso(day)}`}
      className={cn("min-h-[92px] border-b border-r border-border p-1 text-left", !inMonth && "bg-muted/20", isOver && "bg-accent-subtle")}>
      <div className={cn("mb-1 text-[11px]", isToday ? "font-bold text-accent" : "text-muted-foreground")}>{day.getDate()}</div>
      {events.map((ev, i) => <EventChip key={`${ev.id}-${i}`} ev={ev} />)}
    </div>
  );
}

export function CalendarView({ onReloadCounts }) {
  const [cursor, setCursor] = useState(() => { const n = new Date(); return { y: n.getFullYear(), m: n.getMonth() }; });
  const [items, setItems] = useState([]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const grid = useMemo(() => monthGrid(cursor.y, cursor.m), [cursor]);

  const load = useCallback(async () => {
    const start = iso(grid[0]); const end = iso(grid[41]);
    try { const d = await apiFetch(`/tasks/calendar?start=${start}&end=${end}`); setItems(d.items || []); }
    catch (e) { toast.error(e.message); }
  }, [grid]);
  useEffect(() => { load(); }, [load]);

  const byDay = useMemo(() => {
    const map = {};
    for (const ev of items) { (map[ev.date] = map[ev.date] || []).push(ev); }
    return map;
  }, [items]);

  const onDragEnd = async (e) => {
    const { active, over } = e;
    if (!over) return;
    const overId = String(over.id);
    if (!overId.startsWith("day::")) return;
    const newDate = overId.slice(5);
    const ev = active.data.current;
    if (!ev || ev.recurring || ev.date === newDate) return;
    try {
      await apiFetch(`/tasks/${ev.id}`, { method: "PUT", body: JSON.stringify({ title: ev.title, due_date: newDate }) });
      toast.success("Task rescheduled");
      load(); onReloadCounts?.();
    } catch (err) { toast.error(err.message); }
  };

  const monthName = new Date(cursor.y, cursor.m, 1).toLocaleString("default", { month: "long", year: "numeric" });
  const shift = (delta) => setCursor((c) => { const d = new Date(c.y, c.m + delta, 1); return { y: d.getFullYear(), m: d.getMonth() }; });

  return (
    <div data-testid="calendar-view">
      <div className="mb-3 flex items-center gap-2">
        <button onClick={() => shift(-1)} className="rounded-lg border border-border p-1.5 hover:bg-muted" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
        <span className="min-w-[160px] text-center font-heading text-lg">{monthName}</span>
        <button onClick={() => shift(1)} className="rounded-lg border border-border p-1.5 hover:bg-muted" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
        <button onClick={() => { const n = new Date(); setCursor({ y: n.getFullYear(), m: n.getMonth() }); }} className="ml-1 rounded-lg border border-border px-2 py-1.5 text-xs hover:bg-muted" data-testid="cal-today">Today</button>
        <span className="ml-auto text-[11px] text-muted-foreground">Drag a task to another day to reschedule</span>
      </div>
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="overflow-hidden rounded-xl border-l border-t border-border">
          <div className="grid grid-cols-7">
            {DOW.map((d) => <div key={d} className="border-b border-r border-border bg-muted/40 px-1 py-1.5 text-center text-[11px] font-semibold text-muted-foreground">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {grid.map((day) => (
              <DayCell key={iso(day)} day={day} inMonth={day.getMonth() === cursor.m}
                isToday={iso(day) === iso(new Date())} events={byDay[iso(day)] || []} />
            ))}
          </div>
        </div>
      </DndContext>
    </div>
  );
}
